const HASH_SCALE = 1_000_000;
function canonicalNumber(value) {
    if (!Number.isFinite(value))
        throw new RangeError("Cannot hash a non-finite number");
    const rounded = Math.round(value * HASH_SCALE);
    return String(Object.is(rounded, -0) ? 0 : rounded);
}
function stableHashSerialize(value) {
    if (value === null)
        return "null";
    switch (typeof value) {
        case "string":
            return JSON.stringify(value);
        case "boolean":
            return value ? "true" : "false";
        case "number":
            return canonicalNumber(value);
        case "object": {
            if (Array.isArray(value)) {
                return `[${value.map(stableHashSerialize).join(",")}]`;
            }
            const record = value;
            return `{${Object.keys(record)
                .sort()
                .map((key) => `${JSON.stringify(key)}:${stableHashSerialize(record[key])}`)
                .join(",")}}`;
        }
        default:
            throw new TypeError(`Unsupported hash value: ${typeof value}`);
    }
}
function stableJsonValue(value, ancestors) {
    if (value === null)
        return "null";
    switch (typeof value) {
        case "string":
            return JSON.stringify(value);
        case "boolean":
            return value ? "true" : "false";
        case "number":
            if (!Number.isFinite(value))
                throw new RangeError("Cannot serialize a non-finite number");
            return JSON.stringify(Object.is(value, -0) ? 0 : value);
        case "object": {
            if (ancestors.has(value))
                throw new TypeError("Cannot serialize a cyclic value");
            ancestors.add(value);
            try {
                if (Array.isArray(value)) {
                    if (Object.getOwnPropertySymbols(value).length > 0) {
                        throw new TypeError("Stable JSON does not support symbol keys");
                    }
                    const keys = Object.keys(value);
                    if (keys.length !== value.length
                        || keys.some((key, index) => key !== String(index))) {
                        throw new TypeError("Stable JSON does not support sparse arrays or array properties");
                    }
                    const entries = [];
                    for (let index = 0; index < value.length; index += 1) {
                        entries.push(stableJsonValue(value[index], ancestors));
                    }
                    return `[${entries.join(",")}]`;
                }
                const prototype = Object.getPrototypeOf(value);
                if (prototype !== Object.prototype && prototype !== null) {
                    throw new TypeError("Stable JSON only supports plain objects and arrays");
                }
                if (Object.getOwnPropertySymbols(value).length > 0) {
                    throw new TypeError("Stable JSON does not support symbol keys");
                }
                const record = value;
                return `{${Object.keys(record)
                    .sort()
                    .map((key) => `${JSON.stringify(key)}:${stableJsonValue(record[key], ancestors)}`)
                    .join(",")}}`;
            }
            finally {
                ancestors.delete(value);
            }
        }
        default:
            throw new TypeError(`Unsupported JSON value: ${typeof value}`);
    }
}
/** Exact, key-sorted JSON for portable replay artifacts and integrity checks. */
export function stableJsonStringify(value) {
    return stableJsonValue(value, new Set());
}
/** Stable FNV-1a over a string's UTF-16 code units. */
export function deterministicStringHash(value) {
    let hash = 0x811c9dc5;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, "0");
}
/** Exact JSON checksum. Unlike state hashes, numbers are not precision-quantized. */
export function deterministicJsonHash(value) {
    return deterministicStringHash(stableJsonStringify(value));
}
/** Stable FNV-1a hash over canonical UTF-16 code units. */
export function deterministicHash(value) {
    return deterministicStringHash(stableHashSerialize(value));
}
export function canonicalGameState(state) {
    return {
        finishReason: state.finishReason,
        lastGoal: state.lastGoal,
        mallets: state.mallets,
        matchId: state.matchId,
        pausedFromPhase: state.pausedFromPhase,
        phase: state.phase,
        phaseTicksRemaining: state.phaseTicksRemaining,
        physicsVersion: state.physicsVersion,
        players: state.players,
        protocolVersion: state.protocolVersion,
        puck: state.puck,
        remainingTicks: state.remainingTicks,
        score: state.score,
        seed: state.seed,
        tick: state.tick,
        winner: state.winner,
    };
}
export function hashGameState(state) {
    return deterministicHash(canonicalGameState(state));
}
export function hashGameConfig(config) {
    return deterministicHash(config);
}
//# sourceMappingURL=hash.js.map